import { Drawer, Text } from '@/components';
import { useBudgetTypes } from '@/features/master-table/hooks';
import { useProject } from '@/features/project/_hooks/project';
import { useUpsertReserveBudgetItem } from '@/features/project/projects/detail/income-and-expenditure/_hooks/expenditure-item';
import { useMessage } from '@/providers';
import { type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectDetailDrawerState } from '../../../_components/ProjectDrawerStateProvider';
import { UpsertPriorReserveBudgetItemForm } from './UpsertPriorReserveBudgetItemForm';

type Props = {
  priorProjectId: string;
};

export const UpsertPriorReserveBudgetItemFormDrawer: FC<Props> = ({
  priorProjectId,
}) => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();

  // NOTE: 前年度のアクセス権がない場合priorProjectはエラーとなる。
  const priorProject = useProject(priorProjectId);
  const budgetTypes = useBudgetTypes();
  const { mutateAsync } = useUpsertReserveBudgetItem();

  const openDrawer =
    drawerState.open && drawerState.type === 'prior-reserve-budget-item';

  const hasReserveBudgetType = budgetTypes.data?.find(
    (budgetType) =>
      budgetType.fiscalYear === priorProject.data?.fiscalYear &&
      budgetType.type === 'reserve',
  );

  useEffect(() => {
    if (
      priorProject.data &&
      budgetTypes.data &&
      openDrawer &&
      !hasReserveBudgetType
    ) {
      openMessage(
        'warning',
        t('warning-message-reserve-budget-type-not-exist'),
      );
      setDrawerState({ open: false });
    }
  }, [
    priorProject.data,
    budgetTypes.data,
    openMessage,
    t,
    setDrawerState,
    hasReserveBudgetType,
    openDrawer,
  ]);

  if (!hasReserveBudgetType) {
    return null;
  }

  return (
    <Drawer
      open={openDrawer}
      onClose={() => {
        setDrawerState({ open: false });
      }}
      destroyOnClose
    >
      {priorProject.data?.subProjects[0]?.expenditureCarts ? (
        <UpsertPriorReserveBudgetItemForm
          fiscalYear={priorProject.data.fiscalYear}
          expenditureCarts={priorProject.data.subProjects[0].expenditureCarts}
          onSubmit={async (values) => {
            if (!priorProject.data) {
              return;
            }
            try {
              await mutateAsync({
                ...values,
                projectId: priorProject.data.id,
                subProjectId: priorProject.data.subProjects[0]?.id ?? '',
              });
              await Promise.all([
                openMessage('success', t('message-update-completed')),
                (async () => {
                  setDrawerState({ open: false });
                })(),
              ]);
            } catch {
              await openMessage('error', t('message-update-failed'));
            }
          }}
        />
      ) : (
        <Text text={t('message-no-available-expenditure-cart')} />
      )}
    </Drawer>
  );
};
